import React, { useState, useMemo } from 'react';
import { 
  MapPin, Home, UtensilsCrossed, Bus, Zap, Gamepad2, Smartphone, Heart, 
  ChevronDown, ChevronUp, Lightbulb, TrendingUp, ArrowRight, Calculator,
  PiggyBank, Info
} from 'lucide-react';
import './CostOfLivingPlanner.css';

// City data with cost multipliers
const CITIES = {
  dublin: {
    id: 'dublin',
    name: 'Dublin',
    icon: '🏰',
    multiplier: 1.0,
    description: 'Capital city, highest cost of living',
    image: 'dublin',
    averages: {
      accommodation: 1200,
      food: 300,
      transport: 120,
      utilities: 80,
      entertainment: 150,
      mobile: 35,
      health: 35,
    },
  },
  cork: {
    id: 'cork',
    name: 'Cork',
    icon: '🏙️',
    multiplier: 0.85,
    description: 'Second largest city, more affordable',
    image: 'cork',
    averages: {
      accommodation: 850,
      food: 280,
      transport: 90,
      utilities: 70,
      entertainment: 120,
      mobile: 35,
      health: 35,
    },
  },
  galway: {
    id: 'galway',
    name: 'Galway',
    icon: '🌊',
    multiplier: 0.80,
    description: 'Cultural hub, student-friendly',
    image: 'galway',
    averages: {
      accommodation: 750,
      food: 260,
      transport: 70,
      utilities: 65,
      entertainment: 110,
      mobile: 35,
      health: 35,
    },
  },
  limerick: {
    id: 'limerick',
    name: 'Limerick',
    icon: '🏛️',
    multiplier: 0.75,
    description: 'Most affordable city, great value',
    image: 'limerick',
    averages: {
      accommodation: 650,
      food: 250,
      transport: 60,
      utilities: 60,
      entertainment: 100,
      mobile: 35,
      health: 35,
    },
  },
};

// Lifestyle presets
const LIFESTYLE_PRESETS = {
  budget: {
    id: 'budget',
    name: 'Budget',
    icon: '💰',
    description: 'Essential living, minimal extras',
    multipliers: {
      accommodation: 0.7,
      food: 0.6,
      transport: 0.7,
      utilities: 0.8,
      entertainment: 0.3,
      mobile: 0.8,
      health: 0.8,
    },
  },
  moderate: {
    id: 'moderate',
    name: 'Moderate',
    icon: '⚖️',
    description: 'Balanced lifestyle with some extras',
    multipliers: {
      accommodation: 1.0,
      food: 1.0,
      transport: 1.0,
      utilities: 1.0,
      entertainment: 0.7,
      mobile: 1.0,
      health: 1.0,
    },
  },
  comfortable: {
    id: 'comfortable',
    name: 'Comfortable',
    icon: '🌟',
    description: 'Comfortable living with amenities',
    multipliers: {
      accommodation: 1.3,
      food: 1.2,
      transport: 1.2,
      utilities: 1.2,
      entertainment: 1.0,
      mobile: 1.2,
      health: 1.2,
    },
  },
};

// Expense categories
const EXPENSE_CATEGORIES = {
  accommodation: {
    id: 'accommodation',
    name: 'Accommodation',
    icon: Home,
    min: 500,
    max: 2000,
    step: 25,
    color: '#10B981',
    description: 'Rent, utilities included in rent',
  },
  food: {
    id: 'food',
    name: 'Food & Groceries',
    icon: UtensilsCrossed,
    min: 150,
    max: 400,
    step: 10,
    color: '#f59e0b',
    description: 'Groceries, dining out',
  },
  transport: {
    id: 'transport',
    name: 'Transport',
    icon: Bus,
    min: 50,
    max: 150,
    step: 5,
    color: '#3b82f6',
    description: 'Public transport, occasional taxi',
  },
  utilities: {
    id: 'utilities',
    name: 'Utilities',
    icon: Zap,
    min: 50,
    max: 100,
    step: 5,
    color: '#8b5cf6',
    description: 'Electricity, gas, water',
  },
  entertainment: {
    id: 'entertainment',
    name: 'Entertainment',
    icon: Gamepad2,
    min: 50,
    max: 200,
    step: 10,
    color: '#ec4899',
    description: 'Movies, outings, hobbies',
  },
  mobile: {
    id: 'mobile',
    name: 'Mobile/Internet',
    icon: Smartphone,
    min: 20,
    max: 50,
    step: 5,
    color: '#06b6d4',
    description: 'Phone plan, broadband',
  },
  health: {
    id: 'health',
    name: 'Health Insurance',
    icon: Heart,
    min: 15,
    max: 50,
    step: 5,
    color: '#ef4444',
    description: 'Private health insurance',
  },
};

// Savings tips
const SAVINGS_TIPS = [
  {
    category: 'accommodation',
    title: 'Share accommodation',
    tip: 'Sharing a 2-bedroom apartment can save €300-500/month compared to living alone.',
  },
  {
    category: 'accommodation',
    title: 'Consider suburbs',
    tip: 'Living 20-30 minutes from city center can reduce rent by 20-30%.',
  },
  {
    category: 'food',
    title: 'Cook at home',
    tip: 'Meal prepping can save €100-150/month compared to eating out regularly.',
  },
  {
    category: 'food',
    title: 'Shop at discount stores',
    tip: 'Aldi and Lidl are significantly cheaper than premium supermarkets.',
  },
  {
    category: 'transport',
    title: 'Get a Leap Card',
    tip: 'Student Leap Card offers 50% discount on public transport.',
  },
  {
    category: 'transport',
    title: 'Walk or cycle',
    tip: 'Dublin and other cities are bike-friendly. Consider cycling for short distances.',
  },
  {
    category: 'entertainment',
    title: 'Student discounts',
    tip: 'Many museums, cinemas, and venues offer student discounts up to 50%.',
  },
  {
    category: 'mobile',
    title: 'Compare plans',
    tip: 'Prepaid plans from giffgaff, 48s, or Tesco Mobile can be cheaper than contracts.',
  },
];

const EUR_TO_INR = 90;

const CostOfLivingPlanner = () => {
  const [selectedCity, setSelectedCity] = useState('dublin');
  const [lifestyle, setLifestyle] = useState('moderate');
  const [expenses, setExpenses] = useState({
    accommodation: 900,
    food: 250,
    transport: 100,
    utilities: 70,
    entertainment: 100,
    mobile: 35,
    health: 30,
  });
  const [currency, setCurrency] = useState('EUR');
  const [showTips, setShowTips] = useState(false);
  const [expandedTip, setExpandedTip] = useState(null);

  // Calculate totals
  const calculations = useMemo(() => {
    const city = CITIES[selectedCity];
    const monthlyTotal = Object.values(expenses).reduce((sum, val) => sum + val, 0);
    const yearlyTotal = monthlyTotal * 12;
    
    // Calculate comparison with city averages
    const comparison = {};
    Object.keys(EXPENSE_CATEGORIES).forEach((key) => {
      const average = city.averages[key];
      const userExpense = expenses[key];
      comparison[key] = {
        average,
        user: userExpense,
        difference: userExpense - average,
        percentage: ((userExpense / average) * 100).toFixed(0),
      };
    });

    return {
      monthlyTotal,
      yearlyTotal,
      comparison,
      inrTotal: monthlyTotal * EUR_TO_INR,
      yearlyInr: yearlyTotal * EUR_TO_INR,
    };
  }, [selectedCity, expenses]);

  // Format currency
  const formatCurrency = (amount, curr = currency) => {
    if (curr === 'INR') {
      return `₹${(amount * EUR_TO_INR).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `€${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Apply lifestyle preset
  const applyPreset = (presetId) => {
    setLifestyle(presetId);
    const preset = LIFESTYLE_PRESETS[presetId];
    const city = CITIES[selectedCity];
    
    const newExpenses = {};
    Object.keys(EXPENSE_CATEGORIES).forEach((key) => {
      const base = city.averages[key];
      const multiplier = preset.multipliers[key];
      const category = EXPENSE_CATEGORIES[key];
      let value = Math.round(base * multiplier);
      value = Math.max(category.min, Math.min(category.max, value));
      newExpenses[key] = value;
    });
    
    setExpenses(newExpenses);
  };

  // Handle city change
  const handleCityChange = (cityId) => {
    setSelectedCity(cityId);
    // Recalculate expenses based on new city
    const preset = LIFESTYLE_PRESETS[lifestyle];
    const city = CITIES[cityId];
    
    const newExpenses = {};
    Object.keys(EXPENSE_CATEGORIES).forEach((key) => {
      const base = city.averages[key];
      const multiplier = preset.multipliers[key];
      const category = EXPENSE_CATEGORIES[key];
      let value = Math.round(base * multiplier);
      value = Math.max(category.min, Math.min(category.max, value));
      newExpenses[key] = value;
    });
    
    setExpenses(newExpenses);
  };

  return (
    <div className="cost-planner">
      <div className="planner-header">
        <h2><Calculator size={28} /> Cost of Living Budget Planner</h2>
        <p className="planner-subtitle">Plan an indicative monthly budget for studying in Ireland</p>
        <p className="planner-disclaimer">Costs and exchange rates are planning estimates. Verify current prices, accommodation terms, and currency rates before committing funds.</p>
        <div className="currency-toggle">
          <button 
            className={`currency-btn ${currency === 'EUR' ? 'active' : ''}`}
            onClick={() => setCurrency('EUR')}
          >
            € EUR
          </button>
          <button 
            className={`currency-btn ${currency === 'INR' ? 'active' : ''}`}
            onClick={() => setCurrency('INR')}
          >
            ₹ INR
          </button>
        </div>
      </div>

      <div className="planner-body">
        {/* City Selection */}
        <section className="city-selection-section">
          <h3><MapPin size={20} /> Select Your City</h3>
          <div className="city-cards-grid">
            {Object.values(CITIES).map((city) => (
              <div
                key={city.id}
                className={`city-card ${selectedCity === city.id ? 'selected' : ''}`}
                onClick={() => handleCityChange(city.id)}
              >
                <div className="city-icon">{city.icon}</div>
                <div className="city-info">
                  <span className="city-name">{city.name}</span>
                  <span className="city-description">{city.description}</span>
                </div>
                <div className="city-multiplier">
                  {city.multiplier === 1.0 ? 'Base' : `${Math.round((1 - city.multiplier) * 100)}% less`}
                </div>
                {selectedCity === city.id && <div className="selected-indicator">✓</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Lifestyle Presets */}
        <section className="lifestyle-section">
          <h3><TrendingUp size={20} /> Lifestyle Preset</h3>
          <div className="lifestyle-buttons">
            {Object.values(LIFESTYLE_PRESETS).map((preset) => (
              <button
                key={preset.id}
                className={`lifestyle-btn ${lifestyle === preset.id ? 'active' : ''}`}
                onClick={() => applyPreset(preset.id)}
              >
                <span className="lifestyle-icon">{preset.icon}</span>
                <span className="lifestyle-name">{preset.name}</span>
                <span className="lifestyle-desc">{preset.description}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Expense Categories */}
        <section className="expenses-section">
          <h3><PiggyBank size={20} /> Monthly Expenses</h3>
          <div className="expenses-grid">
            {Object.values(EXPENSE_CATEGORIES).map((category) => {
              const IconComponent = category.icon;
              return (
                <div key={category.id} className="expense-card">
                  <div className="expense-header" style={{ borderColor: category.color }}>
                    <div className="expense-title">
                      <IconComponent size={18} style={{ color: category.color }} />
                      <span>{category.name}</span>
                    </div>
                    <span className="expense-value" style={{ color: category.color }}>
                      {formatCurrency(expenses[category.id])}
                    </span>
                  </div>
                  <div className="expense-slider-container">
                    <input
                      type="range"
                      min={category.min}
                      max={category.max}
                      step={category.step}
                      value={expenses[category.id]}
                      onChange={(e) => setExpenses({ ...expenses, [category.id]: Number(e.target.value) })}
                      className="expense-slider"
                      style={{ '--accent-color': category.color }}
                    />
                    <div className="expense-range-labels">
                      <span>€{category.min}</span>
                      <span>€{category.max}</span>
                    </div>
                  </div>
                  <div className="expense-description">{category.description}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Summary Card */}
        <section className="summary-section">
          <div className="summary-card">
            <div className="summary-header">
              <h3>Monthly Budget Summary</h3>
              <span className="city-badge">{CITIES[selectedCity].name}</span>
            </div>
            
            <div className="summary-totals">
              <div className="total-item primary">
                <span className="total-label">Monthly Total</span>
                <span className="total-value">{formatCurrency(calculations.monthlyTotal)}</span>
                {currency === 'EUR' && (
                  <span className="total-inr">≈ ₹{calculations.inrTotal.toLocaleString('en-IN')}</span>
                )}
              </div>
              <div className="total-item">
                <span className="total-label">Yearly Total</span>
                <span className="total-value">{formatCurrency(calculations.yearlyTotal)}</span>
                {currency === 'EUR' && (
                  <span className="total-inr">≈ ₹{calculations.yearlyInr.toLocaleString('en-IN')}</span>
                )}
              </div>
            </div>

            {/* Comparison Chart */}
            <div className="comparison-section">
              <h4>Your Budget vs City Average</h4>
              <div className="comparison-bars">
                {Object.values(EXPENSE_CATEGORIES).map((category) => {
                  const comp = calculations.comparison[category.id];
                  const IconComponent = category.icon;
                  return (
                    <div key={category.id} className="comparison-row">
                      <div className="comparison-label">
                        <IconComponent size={14} style={{ color: category.color }} />
                        <span>{category.name}</span>
                      </div>
                      <div className="comparison-bar-container">
                        <div className="comparison-bar-wrapper">
                          <span className="bar-label">Avg</span>
                          <div className="comparison-bar average" style={{ 
                            width: `${Math.min(100, (comp.average / category.max) * 100)}%`,
                            backgroundColor: `${category.color}40`
                          }}>
                            <span className="bar-value">€{comp.average}</span>
                          </div>
                        </div>
                        <div className="comparison-bar-wrapper">
                          <span className="bar-label">You</span>
                          <div className="comparison-bar user" style={{ 
                            width: `${Math.min(100, (comp.user / category.max) * 100)}%`,
                            backgroundColor: category.color
                          }}>
                            <span className="bar-value">€{comp.user}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`comparison-diff ${comp.difference > 0 ? 'higher' : 'lower'}`}>
                        {comp.difference > 0 ? '+' : ''}{comp.difference}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Savings Tips */}
        <section className="tips-section">
          <button 
            className="section-toggle"
            onClick={() => setShowTips(!showTips)}
          >
            <Lightbulb size={20} />
            <span>Money-Saving Tips</span>
            {showTips ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showTips && (
            <div className="tips-content">
              <div className="tips-accordion">
                {SAVINGS_TIPS.map((tip, index) => (
                  <div key={index} className="tip-item">
                    <button
                      className="tip-header"
                      onClick={() => setExpandedTip(expandedTip === index ? null : index)}
                    >
                      <span className="tip-category-icon" style={{ color: EXPENSE_CATEGORIES[tip.category].color }}>
                        {React.createElement(EXPENSE_CATEGORIES[tip.category].icon, { size: 16 })}
                      </span>
                      <span className="tip-title">{tip.title}</span>
                      {expandedTip === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedTip === index && (
                      <div className="tip-body">
                        <p>{tip.tip}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Quick Info */}
        <section className="info-section">
          <h4><Info size={18} /> Important Notes</h4>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-icon">🏠</span>
              <span>Student accommodation is limited - apply early</span>
            </div>
            <div className="info-item">
              <span className="info-icon">📚</span>
              <span>Books and supplies: €50-100/month extra</span>
            </div>
            <div className="info-item">
              <span className="info-icon">✈️</span>
              <span>Flights to India: €400-800 round trip</span>
            </div>
            <div className="info-item">
              <span className="info-icon">💱</span>
              <span>Exchange rate used: 1 EUR = ₹{EUR_TO_INR}</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="planner-cta">
          <h4>Want More Details?</h4>
          <p>Read our comprehensive guide on cost of living in Ireland for students, including accommodation tips and part-time job opportunities.</p>
          <a href="/resources/cost-of-living-ireland-students" className="cta-button">
            Read Complete Guide <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default CostOfLivingPlanner;
